import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [usersRes, contentRes, engagementRes] = await Promise.all([
        analyticsAPI.getUsers({ period }),
        analyticsAPI.getContent(),
        analyticsAPI.getEngagement({ period }),
      ]);

      console.log('Analytics responses:', { users: usersRes, content: contentRes, engagement: engagementRes });

      setAnalytics({
        users: usersRes?.data?.data || null,
        content: contentRes?.data?.data || null,
        engagement: engagementRes?.data?.data || null,
      });
    } catch (error) {
      console.error('Analytics error:', error);
      setToast({ show: true, message: 'Failed to fetch analytics', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Analytics</h2>
            <p className="page-subtitle">Insights and metrics overview</p>
          </div>
          <div className="d-flex gap-2">
            <select 
              className="form-select" 
              style={{ width: 'auto' }} 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
            <button className="btn btn-outline-primary" onClick={fetchAnalytics}>
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !analytics ? (
        <div className="text-center py-5">
          <i className="bi bi-bar-chart fs-1 text-muted d-block mb-3"></i>
          <p className="text-muted">No analytics data available</p>
        </div>
      ) : (
        <>
          {/* User Analytics */}
          <div className="stat-card mb-4">
            <h5 className="mb-3"><i className="bi bi-people me-2"></i>User Growth</h5>
            <div className="row">
              <div className="col-md-3">
                <small className="text-muted d-block">Total Users</small>
                <h3 className="mb-0">{analytics.users?.overview?.total_users || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Active Users</small>
                <h3 className="mb-0">{analytics.users?.overview?.active_users || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Premium Users</small>
                <h3 className="mb-0">{analytics.users?.premium_stats?.premium_count || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">New This Month</small>
                <h3 className="mb-0">{analytics.users?.overview?.new_users || 0}</h3>
              </div>
            </div>
          </div>

          {/* Content Analytics */}
          <div className="stat-card mb-4">
            <h5 className="mb-3"><i className="bi bi-file-text me-2"></i>Content Performance</h5>
            <div className="row">
              <div className="col-md-4">
                <small className="text-muted d-block">Total Articles</small>
                <h3 className="mb-0">{analytics.content?.overview?.total_articles || 0}</h3>
              </div>
              <div className="col-md-4">
                <small className="text-muted d-block">Total Duas</small>
                <h3 className="mb-0">{analytics.content?.overview?.total_duas || 0}</h3>
              </div>
              <div className="col-md-4">
                <small className="text-muted d-block">Total Views</small>
                <h3 className="mb-0">{analytics.content?.overview?.total_views || 0}</h3>
              </div>
            </div>
          </div>

          {/* Engagement Analytics */}
          <div className="stat-card">
            <h5 className="mb-3"><i className="bi bi-heart me-2"></i>User Engagement</h5>
            <div className="row">
              <div className="col-md-3">
                <small className="text-muted d-block">Daily Logs</small>
                <h3 className="mb-0">{analytics.engagement?.overview?.daily_logs_count || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Prayer Completions</small>
                <h3 className="mb-0">{analytics.engagement?.overview?.prayer_completions || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Habit Completions</small>
                <h3 className="mb-0">{analytics.engagement?.overview?.habit_completions || 0}</h3>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Active Habits</small>
                <h3 className="mb-0">{analytics.engagement?.overview?.active_habits || 0}</h3>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;